import { FC, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import DataTable from 'components/tables/data-table';
import { useAuth } from 'contexts';
import { downloadUngroupedCsv, fetchActiveUnitList, fetchActiveUnitsUngrouped } from 'services';
import { downloadCsv } from 'services/download-csv';
import { FilterState } from 'utils';

import { Role } from 'types';
import stackDataTableColumns, { defaultStackColumns } from './stack-columns';
import unitDataColumns from './unit-columns';
import './unit-list.scss';

const isFilterState = (val?: any): val is FilterState =>
  typeof val === 'object' && 'logicalOperator' in val && 'filters' in val && 0 in val.filters;

const UnitList: FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { userProfile } = useAuth();

  /* initialize to unstacked if initial filters applied */
  const [stacked, setStacked] = useState(!isFilterState(location.state?.filterState));
  const fetchUnstacked = useMemo(() => fetchActiveUnitsUngrouped({ active: true }), []);
  const downloadUnstacked = useMemo(() => downloadUngroupedCsv({ active: true }), []);

  const initialColumns = useMemo(() => {
    if (stacked) {
      return (userProfile.columnPreferences ?? []).filter(cp => cp.table === 'UNIT_ASSOCIATED').length > 0
        ? userProfile.columnPreferences
            .filter(cp => cp.table === 'UNIT_ASSOCIATED')
            .map(({ column, width }) => {
              return { field: column, width };
            })
        : defaultStackColumns.map(c => {
            return { field: c, width: 80 };
          });
    } else {
      return (userProfile.columnPreferences ?? []).filter(cp => cp.table === 'UNIT_UNSTACKED').length > 0
        ? userProfile.columnPreferences
            .filter(cp => cp.table === 'UNIT_UNSTACKED')
            .map(({ column, width }) => {
              return { field: column, width };
            })
        : [
            'unitNumber',
            'booking',
            'customer',
            'receivedDate',
            'sizeType',
            'unitStatus',
            'statusReason',
            'licensePlate',
            'loaded',
            'notes',
            'onHold',
            'holdNotes',
            'carbCompliant'
          ].map(c => {
            return { field: c, width: 80 };
          });
    }
  }, [stacked, userProfile.columnPreferences]);

  const initialFilters: FilterState | undefined = useMemo(() => {
    const filterState = location.state?.filterState;
    if (!isFilterState(filterState)) {
      return undefined;
    }
    return {
      ...filterState,
      filters: stacked
        ? filterState.filters.filter(filter => stackDataTableColumns.some(column => column.field === filter.field))
        : filterState.filters.filter(filter => unitDataColumns.some(column => column.field === filter.field))
    };
  }, [location.state, stacked]);

  return (
    <div className="page" id="units">
      <div className="page-header">
        <h1>YARD MANAGEMENT</h1>
        <div className="d-flex g-3 align-items-center">
          <label className="text-big">GROUP ASSOCIATED</label>
          <label className="switch">
            <input type="checkbox" checked={stacked} onChange={e => setStacked(e.target.checked)} />
            <span className="slider round" />
          </label>
          {[Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING].includes(userProfile.role) && (
            <button className="btn-outline" onClick={() => navigate('/units/bulk')}>
              BULK UPDATE
            </button>
          )}
        </div>
      </div>
      <div className="page-content">
        {stacked ? (
          <DataTable
            tableClass="t-height"
            fetchData={fetchActiveUnitList}
            fetchCsv={downloadCsv('units')}
            columns={stackDataTableColumns}
            initialFilters={initialFilters}
            initialPage={{ pageNum: 0, pageSize: 100 }}
            filterByLocation
            initialColumns={initialColumns}
            enableSaveColumns="UNIT_ASSOCIATED"
            showColumnsCategorized
            columnCategories={['Common', 'Container', 'Chassis', 'Genset']}
          />
        ) : (
          <DataTable
            key="ungrouped"
            fetchData={fetchUnstacked}
            fetchCsv={downloadUnstacked}
            columns={unitDataColumns}
            initialFilters={initialFilters}
            initialColumns={initialColumns}
            filterByLocation
            enableSaveColumns="UNIT_UNSTACKED"
            showColumnsCategorized
            columnCategories={['Common', 'Container', 'Chassis', 'Genset']}
          />
        )}
      </div>
    </div>
  );
};

export default UnitList;
